import { PAYMENT_COMPLETED_ID, PAYMENT_DUE_ID } from '@m/constants/TransactionPaymentStatus';

import type { IdNameObject } from '@m/types';

/**
 * Client side of the payment claim guard (EN-14376 / EN-14381).
 *
 * site-api answers a second payment attempt on a visit that is already being charged with
 * HTTP 423 (`ResourcedLocked` / `IngestStatusLocked`, response error code id 10). A visit that
 * is already paid is rejected with an ordinary 4xx whose body does not say "already paid", so the
 * client confirms that case by re-reading the visit instead of parsing the message.
 */

export const PAYMENT_LOCKED_HTTP_STATUS = 423;

/** `ResponseErrorCode.LOCKED` in site-api, mirrored by `generateErrorCodeAlert` in the specialist app. */
export const PAYMENT_LOCKED_RESPONSE_CODE_ID = 10;

export const PAYMENT_IN_PROGRESS_MESSAGE = 'Payment in progress, please wait';
export const PAYMENT_ALREADY_RECEIVED_MESSAGE = 'Payment already received';

/** How long a client keeps re-reading a visit after a 423 before giving up (payment stays disabled). */
export const PAYMENT_IN_PROGRESS_POLL_INTERVAL_MS = 2_000;
export const PAYMENT_IN_PROGRESS_POLL_TIMEOUT_MS = 90_000;

/**
 * - `in_progress`: the server refused because another payment holds the claim. Terminal for this
 *   client: do not retry, wait for the visit to flip to paid.
 * - `already_paid`: the visit is no longer payment due. Nothing left to collect.
 * - `failed`: a real failure; the usual error UI applies and a retry is allowed.
 */
export type PaymentFailureKind = 'in_progress' | 'already_paid' | 'failed';

type VisitPaymentState = {
  open?: boolean;
  transactionPaymentStatus?: IdNameObject | null;
};

const toStatusNumber = (value: unknown): number | undefined => {
  const status = Number(value);
  return Number.isFinite(status) && status > 0 ? status : undefined;
};

/**
 * True when a payment call was rejected with the claim lock. Accepts anything a pay call can
 * produce: `ApiResponseError` / `FetchError` (thrown by `apiFetchV2` and `nonApiResponseFetch`,
 * carry a numeric `status`) or a legacy `apiFetch` failure body (`{ success: false, status, code }`).
 */
export function isPaymentInProgressError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;

  const { status, code } = error as { code?: { id?: unknown }; status?: unknown };

  if (toStatusNumber(status) === PAYMENT_LOCKED_HTTP_STATUS) return true;

  const codeId = toStatusNumber(code?.id);
  return codeId === PAYMENT_LOCKED_HTTP_STATUS || codeId === PAYMENT_LOCKED_RESPONSE_CODE_ID;
}

/** A visit can be paid only while it is open and in `PAYMENT_DUE`. Mirrors the server guard. */
export function isVisitPaymentDue(visit: VisitPaymentState | null | undefined): boolean {
  if (!visit) return false;
  return visit.open !== false && visit.transactionPaymentStatus?.id === PAYMENT_DUE_ID;
}

export function isVisitPaymentCompleted(visit: VisitPaymentState | null | undefined): boolean {
  return visit?.transactionPaymentStatus?.id === PAYMENT_COMPLETED_ID;
}

/**
 * Decide what to show after a pay call failed.
 *
 * A 423 is classified without touching the network. Anything else re-reads the visit through
 * `refreshVisit`: if it is no longer payment due the money was collected by another path (or the
 * visit was closed) and the client must not offer to pay again. If the refresh itself fails or the
 * visit is still due, the failure is a genuine one.
 */
export async function classifyPaymentFailure({
  error,
  refreshVisit,
}: {
  error: unknown;
  refreshVisit: () => Promise<VisitPaymentState | null | undefined>;
}): Promise<PaymentFailureKind> {
  if (isPaymentInProgressError(error)) return 'in_progress';

  let visit: VisitPaymentState | null | undefined;
  try {
    visit = await refreshVisit();
  } catch {
    return 'failed';
  }

  if (visit && !isVisitPaymentDue(visit)) return 'already_paid';
  return 'failed';
}
